import { Appearance, Inventory } from "game-common/models"
import { Graphics } from "pixi.js"

import { ClientGameLogic } from "../../../../client_game_logic"
import { stageViewport } from "../../../../canvas_layout"
import { GuiManager } from "../gui_manager"
import { ensureIntroFont } from "../intro/intro_theme"
import { getRendererApp } from "../renderer_app"
import { pageScale } from "./character_creation/creation_layout"
import { CreationPage, KeyboardSource } from "./character_creation/creation_page"
import { FullScreenModal } from "./fullscreen_modal"

// The character-creation screen: one page, aboard the exiles' ship the night before the wreck, where a
// new player makes their character and journeys onward into chapter 0. The page is
// character_creation/creation_page.ts; this is the modal round it.
//
// The page is built once per opening and kept across repaints of the modal (a resize, the player's
// data landing), so its state and its cached art survive them.

export class CharacterBuilder extends FullScreenModal {
    logic: ClientGameLogic

    private page?: CreationPage
    private wasOpen = false
    private viewportKey = ""
    private fontRequested = false

    constructor(guiManager: GuiManager, logic: ClientGameLogic) {
        // No padding: the page's own coordinates are the visible stage's.
        super(guiManager, { opaque: true, unescapable: true, padding: 0 })
        this.logic = logic
    }

    // The HUD calls this whenever the player entity changes. The page only needs building once the
    // player's appearance and starting kit have arrived, and the modal only needs repainting when it
    // opens or the screen changes size.
    update = () => {
        const open = this.isOpen()
        if (!open) {
            this.release()
            return
        }
        const opening = !this.wasOpen
        if (!this.page && !this.createPage()) {
            // Player data hasn't landed yet; the next update() tries again.
            if (opening) this.repaint()
            return
        }
        this.wasOpen = true
        if (opening) {
            this.page.enter()
            this.repaint()
        } else if (this.viewportKey !== this.currentViewportKey()) {
            this.repaint()
        }
    }

    // Frame hook (driven by GuiManager.updateTimer).
    animate = (dtMs: number) => {
        if (!this.isOpen()) {
            this.release()
            return
        }
        if (!this.page) {
            return
        }
        if (this.viewportKey !== this.currentViewportKey()) {
            this.repaint()
        }
        this.page.animate(dtMs)
    }

    submitCharacter = (appearance: Appearance, inventory: Inventory) => {
        this.logic.playerController.client.saveAppearance(appearance, inventory)
    }

    createContent = (): Graphics => {
        const main = new Graphics()
        const viewport = stageViewport()
        this.viewportKey = this.currentViewportKey()
        main.beginFill(0x05060a, 1)
        main.drawRect(0, 0, viewport.width, viewport.height)
        main.endFill()
        if (this.page) {
            this.page.fit(viewport, this.scale())
            main.addChild(this.page)
        }
        return main
    }

    private createPage = (): boolean => {
        const player = this.guiManager.getPlayer()
        if (!player?.appearance || player.inventory?.items?.length < 1) {
            return false
        }
        this.page = new CreationPage(
            player,
            {
                submitCharacter: this.submitCharacter,
                close: this.finish,
            },
            { keyboard: this.keyboard(), canvas: this.canvas },
        )
        // The words are set in Jost. The login screen normally has it loaded already; a player who never saw
        // that screen gets the page's words set again once it arrives.
        if (!this.fontRequested) {
            this.fontRequested = true
            ensureIntroFont().then(() => this.page?.refreshTexts())
        }
        return true
    }

    /** Journey Onward has played out: close the screen (the close callback finalizes the character). */
    private finish = () => {
        this.release()
        this.close()
    }

    /**
     * The screen has closed, by Journey Onward or any other way (a replay closes it when the recorded player
     * finalized): drop the page, with its caches and its keyboard and wheel listeners. Opening again builds
     * a new one.
     */
    private release = () => {
        const page = this.page
        this.page = undefined
        this.wasOpen = false
        page?.dispose()
    }

    // The modal's repaint frees the Text textures of whatever it holds. The page keeps its own words
    // across repaints, so it steps out first and the new content takes it back.
    private repaint = () => {
        this.page?.parent?.removeChild(this.page)
        this.rebuildSurface()
    }

    private currentViewportKey = () => {
        const v = stageViewport()
        return `${v.left}|${v.top}|${v.right}|${v.bottom}|${this.scale()}`
    }

    /**
     * How much larger than the stage to draw the page: on a phone the stage is shown at about half desktop
     * size, too small to read or tap. Measured as the canvas's height on screen over the stage's.
     */
    private scale = (): number => {
        const canvas = this.canvas()
        const height = canvas?.getBoundingClientRect?.().height
        if (!height) {
            return 1
        }
        const viewport = stageViewport()
        return pageScale(height / viewport.height, viewport)
    }

    private keyboard = (): KeyboardSource | undefined => {
        try {
            return this.logic?.clientRenderer?.keyboard?.()
        } catch (err) {
            return undefined
        }
    }

    private canvas = (): HTMLCanvasElement | undefined => {
        try {
            return getRendererApp().view
        } catch (err) {
            return undefined
        }
    }
}
